// =============================================
// musicApi — scoped, authenticated CRUD for the music tables
// =============================================
// Drop-in replacements for dataAccess.loadTable/addRow/saveRow that talk to the
// per-user /api/music/table/* endpoints (so account owners persist their library
// server-side — the generic /api/db CRUD is admin-only). When the API or a token
// isn't available (pure localStorage / offline mode) these fall back to dataAccess,
// keeping the local-only path working. The returned rows are mirrored into the same
// local cache the rest of the app reads via getTableData().
// =============================================
import {
  loadTable as dbLoadTable, addRow as dbAddRow, saveRow as dbSaveRow,
  getTableData, saveTableData, checkApi,
} from '../db-viewer/data/dataAccess';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const MUSIC_PK: Record<string, string> = {
  mus_tracks_all: 'track_id', mus_artist: 'artist_id', mus_album: 'album_id',
  mus_genre: 'genre_id', mus_mood: 'mood_id', mus_user_owned: 'user_owned_id',
  mus_playlist: 'playlist_id', mus_playlist_song: 'playlist_song_id',
  mus_wishlist: 'wishlist_id', mus_recently_played: 'recently_played_id',
};

function authToken(): string | null {
  return localStorage.getItem('akt_auth_token');
}

async function apiReady(): Promise<boolean> {
  return !!authToken() && (await checkApi());
}

function authHeaders(json = false): Record<string, string> {
  return {
    ...(json ? { 'Content-Type': 'application/json' } : {}),
    Authorization: `Bearer ${authToken()}`,
  };
}

/** A server error (4xx/5xx with a body) — surface it; network errors fall back to local. */
function isNetworkLevel(err: any): boolean {
  return !(err && typeof err.status === 'number');
}

async function asError(res: Response): Promise<Error> {
  const body = await res.json().catch(() => ({} as any));
  const e = new Error(body.message || `Music API ${res.status}`);
  (e as any).status = res.status;
  return e;
}

/** Load a music table — scoped to the current user server-side, mirrored to cache. */
export async function loadTable(name: string): Promise<Record<string, any>[]> {
  if (await apiReady()) {
    try {
      const res = await fetch(`${API_BASE}/music/table/${name}`, { headers: authHeaders() });
      if (!res.ok) throw await asError(res);
      const rows = await res.json();
      try { saveTableData(name, rows); } catch { /* quota — best effort */ }
      return rows;
    } catch {
      // Reads fall back to the local cache on ANY error (matches dataAccess.loadTable)
      // so one failing table never blocks the whole music page.
    }
  }
  return dbLoadTable(name);
}

/** Insert into a music table — ownership enforced server-side; mirrors to cache. */
export async function addRow(name: string, row: Record<string, any>, pk?: string): Promise<Record<string, any>> {
  if (await apiReady()) {
    try {
      const res = await fetch(`${API_BASE}/music/table/${name}`, {
        method: 'POST', headers: authHeaders(true), body: JSON.stringify(row),
      });
      if (!res.ok) throw await asError(res);
      const saved = await res.json();
      try { const d = getTableData(name); d.push(saved); saveTableData(name, d); } catch { /* best effort */ }
      return saved;
    } catch (err) {
      if (!isNetworkLevel(err)) throw err;
    }
  }
  return dbAddRow(name, row, pk);
}

/** Update a music table row — ownership enforced server-side; patches the cache. */
export async function saveRow(name: string, id: number | string, updates: Record<string, any>, pk?: string): Promise<Record<string, any>> {
  if (await apiReady()) {
    try {
      const res = await fetch(`${API_BASE}/music/table/${name}/${id}`, {
        method: 'PUT', headers: authHeaders(true), body: JSON.stringify(updates),
      });
      if (!res.ok) throw await asError(res);
      const saved = await res.json();
      try {
        const key = pk || MUSIC_PK[name];
        const d = getTableData(name);
        // eslint-disable-next-line eqeqeq
        const i = d.findIndex(r => r[key] == id);
        if (i >= 0) { d[i] = { ...d[i], ...saved }; saveTableData(name, d); }
      } catch { /* best effort */ }
      return saved;
    } catch (err) {
      if (!isNetworkLevel(err)) throw err;
    }
  }
  return dbSaveRow(name, id, updates, pk);
}
